/**
 * on-demand.js
 * Functionality for the filterable list of videos on the On Demand event tab.
 */

/* eslint-disable camelcase */

import { format } from 'date-fns';
import { toggleNoResults, serialize } from './filter-lists';

// Elements
const form = document.getElementById( 'on-demand-form' );
const results = document.getElementById( 'on-demand-form-results' );

// Data
let cards = [];
let previousData = {};

/**
 * Create the updated results.
 *
 * @param {Object} sections Object of sorted sections.
 * @param {string} sort     The type of sorting used. For creating appropriate headings.
 * @returns {NodeList}      Updated node list to use.
 */
function createUpdatedResults ( sections, sort ) {
	const updated = document.createElement( 'div' );

	// Loop the sections.
	for ( const key in sections ) {
		if ( Object.hasOwnProperty.call( sections, key ) ) {
			let heading = '';

			// Update heading by sort type.
			switch ( sort ) {
					case 'date': {
						heading = format( new Date( key ), 'MMMM do, yyyy' );
						break;
					}
					case 'track': {
						const select = document.querySelector( 'select[name="tracks"]' );
						const option = select.querySelector( `option[value="${key}"]` );
						heading = option.textContent;
						break;
					}
			}

			// Create a section.
			const section = document.createElement( 'section' );
			section.classList.add( 'pb-5' );

			// Create the heading for the section.
			const h2 = document.createElement( 'h2' );
			h2.classList.add( 'h3' );
			h2.textContent = heading;

			// Create the row for the items.
			const row = document.createElement( 'div' );
			row.classList.add( 'row','row-cols-1','row-cols-sm-2','row-cols-md-3','mx-ng' );

			// Create containers, move items, add to row.
			sections[key].forEach( item => {
				const container = document.createElement( 'div' );

				container.classList.add( 'on-demand__item', 'col', 'p-g' );
				container.appendChild( item );

				row.appendChild( container );
			} );

			// Add elements to section.
			section.appendChild( h2 );
			section.appendChild( row );

			// Add to updated.
			updated.appendChild( section );
		}
	}

	// Updated node list.
	return updated.childNodes;
}

/**
 * Sort the cards and sections.
 * Handles the order in which items and sections appear.
 *
 * @param {object} data The serialized form data.
 */
function sortCardsAndSections( data ) {
	const {sort} = data;

	// Set up sections object.
	const sections = {};

	// Update sections object based on sort value.
	switch ( sort ) {
			case 'date':
				cards.sort( ( a, b ) => {
					return new Date( a.dataset.presentationDate ).getTime() - new Date( b.dataset.presentationDate ).getTime();
				} );

				for ( let index = 0; index < cards.length; index++ ) {
					const card = cards[index];
					const {presentationDate} = card.dataset;
					const day = format( new Date( presentationDate ), 'yyyy-MM-dd\'T\'00:00:01' );

					if ( ! sections[day] ) {
						sections[day] = [];
					}

					sections[day].push( card );
				}
				break;
			case 'track':
				cards.sort( ( a, b ) => {
					return parseInt( a.dataset.tracks ) - parseInt( b.dataset.tracks );
				} );

				for ( let index = 0; index < cards.length; index++ ) {
					const card = cards[index];
					const track = card.dataset.tracks;

					if ( ! sections[track] ) {
						sections[track] = [];
					}

					sections[track].push( card );
				}
				break;
	}

	// Get the updated results node list.
	const updatedResults = createUpdatedResults( sections, sort );

	// Replace the current children with the updated node list.
	results.replaceChildren( ...updatedResults );
}

/**
 * Filter the cards and sections.
 * Handles the visibility of items and sections.
 *
 * @param {object} data The serialized form data.
 */
function filterCardsAndSections( data ) {
	// Get the data.
	const { tracks, research_area } = data;

	// Loop the cards.
	cards.forEach( card => {
		const trackMatches = '' === tracks || card.dataset.tracks.includes( tracks );
		const researchAreaMatches = '' === research_area || card.dataset.researchAreas.includes( research_area );

		if ( trackMatches && researchAreaMatches ) {
			card.removeAttribute( 'hidden' );
			card.parentElement.classList.remove( 'd-none' );
		} else {
			card.setAttribute( 'hidden', true );
			card.parentElement.classList.add( 'd-none' );
		}
	} );

	// Clean up sections
	const sections = Array.from( results.querySelectorAll( 'section' ) );
	sections.forEach( section => {
		const hasVisibleItems = Array.from( section.querySelectorAll( '.card-event-video:not([hidden]' ) );

		if ( hasVisibleItems.length ) {
			section.removeAttribute( 'hidden' );
		} else {
			section.setAttribute( 'hidden', true );
		}
	} );
}

/**
 * Add or remove the no results message.
 */
function updateNoResults() {
	const showNoResults = 0 === Array.from( results.querySelectorAll( 'section:not([hidden])' ) ).length;

	toggleNoResults( showNoResults, results );
}

/**
 * Handle form change.
 */
const handleFormChange = () => {
	const currentData = serialize( form );

	// Only update sort if the value changed.
	// Otherwise we must be filtering.
	if ( currentData.sort !== previousData.sort ) {
		sortCardsAndSections( currentData );
	}

	// Filter no matter what.
	// We'll need this if someone sorts so the data is always up to date.
	filterCardsAndSections( currentData );

	// Add or remove the no results message.
	updateNoResults( results );

	// Update state data.
	previousData = currentData;
};

/**
 * Bind events.
 */
const bind = () => {
	form.addEventListener( 'change', handleFormChange );
};

/**
 * Init.
 */
const init = () => {
	// No form? Bail.
	if ( !form ) {
		return;
	}

	// Set up data.
	cards = Array.from( document.querySelectorAll( '.card-event-video' ) );
	previousData = serialize( form );

	// Set up attributes.
	results.setAttribute( 'aria-live', 'polite' );
	results.setAttribute( 'role', 'region' );

	// Bind events.
	bind();
};

export default init;
